import {
  PaymentMethodType,
  isCard,
  isDigitalWallet,
  isOffSessionUsageSupported,
} from "@circle-react/helpers/paywallCheckoutHelpers";
import type { StripeElementsUpdateOptions } from "./types";
import { stripeElementsSetupFutureUsages } from "./types";

export const resolveStripeElementsPaymentMethodTypes = (
  isLinkCapabilityActive: boolean,
  paymentMethodType: string,
): StripeElementsUpdateOptions["payment_method_types"] => {
  // We have observed that Stripe Elements reports 'link' payments as 'card' `payment_method_type`.
  //
  // This function provides the `payment_method_types` used by the `useOnSubmitWithPMC` hook to
  // *temporarily* reconfigure the Stripe Elements component immediately before client-side
  // validation and payment confirmation.
  //
  // If a user selects Link (which Elements then reports as 'card'), and if we were to only pass
  // ['card'] to `elements.update()`, the Stripe Elements component would be temporarily reconfigured
  // to *only* support card payments. Consequently, the subsequent `elements.submit()` call (which
  // performs client-side validations) would fail. This is because 'link' would no longer be an
  // allowed payment method within the reconfigured Elements, despite the user's intent to use Link.
  //
  // To prevent this failure, when the reported `paymentMethodType` is 'card' (which includes
  // actual card payments and Link payments reported as 'card'), we return `[PaymentMethodType.CARD,
  // PaymentMethodType.LINK]`. This ensures that during the temporary reconfiguration by
  // `useOnSubmitWithPMC`, both 'card' and 'link' are considered allowed payment method types by
  // the Stripe Elements component. This allows `elements.submit()` and the subsequent payment
  // confirmation to succeed for both scenarios.
  //
  // *Important:*
  // We have only been able to reproduce this issue when in `subscription` mode and using Link as
  // the payment method. It does not seem to be reproducible in `payment` and `setup` modes.
  if (isCard(paymentMethodType)) {
    return isLinkCapabilityActive
      ? [PaymentMethodType.CARD, PaymentMethodType.LINK]
      : [PaymentMethodType.CARD];
  }
  // Apple and Google Pay are valid payment methods but cannot be included as options when updating
  // the Elements component because they are included under the "card" capability.
  if (isDigitalWallet(paymentMethodType)) {
    return [PaymentMethodType.CARD];
  }
  return [paymentMethodType];
};

export const resolveStripeElementsSetupFutureUsage = (
  paymentMethodType: string,
) =>
  isOffSessionUsageSupported(paymentMethodType)
    ? stripeElementsSetupFutureUsages.off_session
    : undefined;
